import { useState } from "react";
import { useToast } from "@/components/base/Toast";

type FormTab = "webhook" | "embed";

export function WebsiteFormSection() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<FormTab>("webhook");
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [buttonText, setButtonText] = useState("Get Free Consultation");
  const [fields, setFields] = useState({
    name: true,
    phone: true,
    email: true,
    message: true,
    budget: false,
    city: false,
  });

  const toggleField = (key: keyof typeof fields) => {
    setFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(
      "https://api.crmpro.in/webhooks/forms/prop-2024-tk8x3m7a2b"
    );
    showToast("Webhook URL copied to clipboard", "success");
  };

  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(
      '<script src="https://cdn.crmpro.in/widget.js"></script>\n<div id="crmpro-form" data-token="prop-2024-tk8x3m7a2b" data-theme="light"></div>'
    );
    showToast("Embed code copied to clipboard", "success");
  };

  const tabs: { key: FormTab; label: string }[] = [
    { key: "webhook", label: "Webhook URL" },
    { key: "embed", label: "Embed Form" },
  ];

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] p-5">
      <h3 className="text-lg font-semibold text-[#111827]">Website Form Integration</h3>
      <p className="text-sm text-[#6b7280] mt-0.5 mb-4">
        Two ways to capture website leads
      </p>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-5">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-[#6b7280] hover:text-[#374151]"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Webhook URL Tab */}
      {activeTab === "webhook" && (
        <div className="space-y-5">
          <p className="text-sm text-[#6b7280]">
            Add this webhook URL to your website&apos;s contact form to automatically send leads to
            CRM Pro
          </p>

          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
              Webhook URL
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value="https://api.crmpro.in/webhooks/forms/prop-2024-tk8x3m7a2b"
                readOnly
                className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-[#374151] font-mono"
              />
              <button
                onClick={handleCopyWebhook}
                className="px-3 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-medium text-[#6b7280]">Verification Token</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#374151] font-mono">tk_****-****-****-m7a2b</span>
              <button
                onClick={() => showToast("New verification token generated", "success")}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap"
              >
                Regenerate
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
              Example Payload
            </label>
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-xs text-green-400 font-mono leading-relaxed">
                <span className="text-gray-500">POST</span>{" "}
                <span className="text-blue-400">/webhooks/forms/prop-2024-tk8x3m7a2b</span>
                {"\n"}
                {"{"}
                {"\n"}  <span className="text-amber-300">"name"</span>:{" "}
                <span className="text-green-300">"Raj Sharma"</span>,
                {"\n"}  <span className="text-amber-300">"phone"</span>:{" "}
                <span className="text-green-300">"9876543210"</span>,
                {"\n"}  <span className="text-amber-300">"email"</span>:{" "}
                <span className="text-green-300">"raj@example.com"</span>,
                {"\n"}  <span className="text-amber-300">"message"</span>:{" "}
                <span className="text-green-300">"Interested in 2BHK flat"</span>,
                {"\n"}  <span className="text-amber-300">"source"</span>:{" "}
                <span className="text-green-300">"website"</span>
                {"\n"}
                {"}"}
              </pre>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
              Supported Fields
            </label>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#6b7280]">
              <span>
                <strong className="text-[#374151]">Required:</strong> name, phone
              </span>
              <span>
                <strong className="text-[#374151]">Optional:</strong> email, message, source, city,
                budget, property_type
              </span>
            </div>
          </div>

          <button
            onClick={() => showToast("Test lead received successfully", "success")}
            className="px-4 py-2 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors whitespace-nowrap"
          >
            Test Webhook
          </button>
        </div>
      )}

      {/* Embed Form Tab */}
      {activeTab === "embed" && (
        <div className="space-y-5">
          <p className="text-sm text-[#6b7280]">
            Copy this code and paste into your website HTML to show a lead capture form
          </p>

          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-1.5">Embed Code</label>
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-xs font-mono leading-relaxed">
                <span className="text-pink-400">
                  &lt;script src=&quot;https://cdn.crmpro.in/widget.js&quot;&gt;&lt;/script&gt;
                </span>
                {"\n"}
                <span className="text-blue-400">
                  &lt;div id=&quot;crmpro-form&quot;{" "}
                </span>
                {"\n"}
                <span className="text-amber-300">
                  {"  "}data-token=&quot;prop-2024-tk8x3m7a2b&quot;
                </span>
                {"\n"}
                <span className="text-amber-300">
                  {"  "}data-theme=&quot;{theme}&quot;
                </span>
                {"\n"}
                <span className="text-blue-400">&gt;</span>
                {"\n"}
                <span className="text-blue-400">&lt;/div&gt;</span>
              </pre>
            </div>
            <button
              onClick={handleCopyEmbed}
              className="mt-2 px-4 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
            >
              Copy Code
            </button>
          </div>

          {/* Customization */}
          <div>
            <label className="block text-xs font-medium text-[#6b7280] mb-2">
              Customization Options
            </label>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#374151]">Theme</span>
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setTheme("light")}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                      theme === "light"
                        ? "bg-white text-[#374151] shadow-sm"
                        : "text-[#6b7280]"
                    }`}
                  >
                    Light
                  </button>
                  <button
                    onClick={() => setTheme("dark")}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                      theme === "dark"
                        ? "bg-white text-[#374151] shadow-sm"
                        : "text-[#6b7280]"
                    }`}
                  >
                    Dark
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-[#374151]">Primary Color</span>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-indigo-600 border-2 border-white ring-2 ring-indigo-600" />
                  <span className="text-xs text-[#6b7280]">#4f46e5</span>
                </div>
              </div>

              <div>
                <span className="text-sm text-[#374151] block mb-2">Fields to show</span>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(fields).map(([key, checked]) => (
                    <label
                      key={key}
                      className="flex items-center gap-1.5 cursor-pointer"
                    >
                      <div
                        onClick={() => toggleField(key as keyof typeof fields)}
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                          checked
                            ? "bg-indigo-600 border-indigo-600"
                            : "border-gray-300"
                        }`}
                      >
                        {checked && (
                          <i className="ri-check-line text-white text-[10px]" />
                        )}
                      </div>
                      <span className="text-xs text-[#374151] capitalize">{key}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Button Text
                </label>
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                  className="w-full max-w-xs border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          <button
            onClick={() => showToast("Form preview opened in new tab", "success")}
            className="px-4 py-2 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors whitespace-nowrap"
          >
            Preview
          </button>
        </div>
      )}
    </div>
  );
}